import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useState } from 'react';
import DiffViewer, { DiffMethod } from 'react-diff-viewer-continued';

import { Button } from '@/components/form/baseInputs/Button';
import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { Switch } from '@/components/form/baseInputs/Switch';
import { Markdown } from '@/components/Markdown/Markdown';
import { Modal } from '@/components/modals/Modal/Modal';
import { PopperContent } from '@/components/poppers/PopperContent';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { COLORS, PRIMITIVES } from '@/styles/tokens/colors';

interface PromptDiffModalProps {
  isOpen: boolean;
  currentText: string;
  suggestedText: string;
  onAcceptSuggestedPrompt: () => void;
  onRejectSuggestedPrompt: () => void;
  isStreaming?: boolean;
  isCreate?: boolean;
}

export function PromptDiffModal({
  isOpen,
  currentText,
  suggestedText,
  onAcceptSuggestedPrompt,
  onRejectSuggestedPrompt,
  isStreaming,
  isCreate = false,
}: PromptDiffModalProps) {
  const theme = useTheme();
  const [splitView, setSplitView] = useState(true);
  const [showOnlyChanges, setShowOnlyChanges] = useState(true);

  const handleClose = useCallback(() => {
    onRejectSuggestedPrompt();
  }, [onRejectSuggestedPrompt]);

  const renderContent = () => {
    if (isStreaming) {
      return (
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="title-xs">
                {isCreate ? 'Writing prompt...' : 'Improving prompt...'}
              </Typography>
            </Stack>
            <Typography variant="body-xs">
              {isCreate
                ? "You'll be able to edit this new prompt once generation is complete."
                : "You'll be able to compare this with the current prompt once generation is complete."}
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: COLORS.GRAY[50],
                border: `1px solid ${COLORS.GRAY[100]}`,
                borderRadius: 1,
                minHeight: '200px',
                maxHeight: '500px',
                overflow: 'auto',
              }}
            >
              {suggestedText ? (
                <Markdown
                  markdown={suggestedText}
                  // Override default EditorInner to remove white background
                  EditorInner={({ children }) => <>{children}</>}
                />
              ) : (
                <Stack spacing={1}>
                  {[95, 80, 100, 70, 85, 90, 75, 100, 65, 80, 95, 70].map(
                    (width, i) => (
                      <Skeleton
                        key={i}
                        variant="text"
                        width={`${width}%`}
                        height={20}
                      />
                    )
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: '70vh',
          border: `1px solid ${COLORS.GRAY[200]}`,
          '& pre': {
            fontFamily: theme.typography.fontFamily,
            fontSize: 14,
            lineHeight: '20px',
            margin: 0,
          },
        }}
      >
        <DiffViewer
          oldValue={currentText}
          newValue={suggestedText}
          splitView={splitView}
          compareMethod={DiffMethod.TRIMMED_LINES}
          hideLineNumbers
          showDiffOnly={showOnlyChanges}
          leftTitle="Current prompt"
          rightTitle="Suggested prompt"
          styles={{
            variables: {
              light: {
                diffViewerBackground: PRIMITIVES.WHITE,
                addedBackground: COLORS.FUNCTIONAL.SUCCESS[50],
                removedBackground: COLORS.FUNCTIONAL.ERROR[50],
                addedGutterBackground: 'transparent',
                removedGutterBackground: 'transparent',
                wordAddedBackground: COLORS.FUNCTIONAL.SUCCESS[100],
                wordRemovedBackground: COLORS.FUNCTIONAL.ERROR[100],
              },
            },
            titleBlock: {
              fontWeight: 600,
              fontSize: 14,
              padding: '8px 12px',
              backgroundColor: COLORS.GRAY[50],
              borderBottom: `1px solid ${COLORS.GRAY[200]}`,
            },
          }}
        />
      </Box>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      heading={isCreate ? 'Generate a prompt' : 'Review suggested changes'}
      dialogContentSx={{ padding: 0 }}
      rightHeaderContent={
        !isStreaming && (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-start"
          >
            <ContextualHelpTooltip>
              <PopperContent body="Compare the original prompt and suggested changes side by side, with red showing removals and green showing additions." />
            </ContextualHelpTooltip>
            <Switch
              label="Show unchanged lines"
              labelPosition="right"
              value={!showOnlyChanges}
              onChange={(e) => setShowOnlyChanges(!e.target.checked)}
            />
            <Switch
              label="Inline view"
              labelPosition="right"
              value={!splitView}
              onChange={(e) => setSplitView(!e.target.checked)}
            />
            <CloseButton
              variant="transparent"
              size="xs"
              onClick={handleClose}
            />
          </Stack>
        )
      }
      actions={
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          {isStreaming ? (
            <Box />
          ) : (
            <Typography variant="body-xs">
              Green = added • Red = removed
            </Typography>
          )}
          <Stack direction="row" spacing={1}>
            <Button
              variant="secondary"
              size="sm"
              onClick={onRejectSuggestedPrompt}
              disabled={isStreaming}
            >
              Keep current
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onAcceptSuggestedPrompt}
              disabled={isStreaming}
              loading={isStreaming}
            >
              Accept changes
            </Button>
          </Stack>
        </Stack>
      }
      maxWidth="lg"
    >
      {renderContent()}
    </Modal>
  );
}
